import { toast } from 'react-toastify';
import Swal from 'sweetalert2';

// Toast notifications (for success/error/info feedback)
export const notify = {
  success: (msg) => toast.success(msg, { position:'top-right', autoClose:3000, hideProgressBar:false }),
  error:   (msg) => toast.error(msg,   { position:'top-right', autoClose:4000, hideProgressBar:false }),
  info:    (msg) => toast.info(msg,    { position:'top-right', autoClose:3000, hideProgressBar:false }),
  warn:    (msg) => toast.warn(msg,    { position:'top-right', autoClose:3500, hideProgressBar:false }),
};

// SweetAlert2 confirmations (for destructive actions)
export const confirm = {
  reject: (title = 'Reject this request?') =>
    Swal.fire({
      title,
      input: 'textarea',
      inputLabel: 'Rejection Remarks *',
      inputPlaceholder: 'Enter reason for rejection...',
      inputAttributes: { rows: 3 },
      showCancelButton: true,
      confirmButtonText: 'Yes, Reject',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      inputValidator: (v) => !v.trim() && 'Remarks are required',
      customClass: { popup: 'swal-popup-rounded' },
    }),

  action: (title, text, confirmText = 'Yes, Proceed', color = '#1a5276') =>
    Swal.fire({
      title,
      text,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: confirmText,
      cancelButtonText: 'Cancel',
      confirmButtonColor: color,
      cancelButtonColor: '#6b7280',
      customClass: { popup: 'swal-popup-rounded' },
    }),

  // Admin password management for a user — choose between setting a new
  // password directly, or emailing the user a secure reset link. Actual
  // current passwords can never be shown (stored as one-way hashes).
  resetPassword: (targetLabel) =>
    Swal.fire({
      title: 'Reset Password',
      html: `
        <div style="text-align:left">
          <div style="background:#eff6ff;border-radius:8px;padding:10px 12px;margin-bottom:16px;font-size:0.82rem;color:#1d4ed8">
            For <b>${targetLabel}</b>. Current passwords can't be viewed (stored as secure hashes) — choose how to reset it instead.
          </div>
          <div style="display:flex;gap:8px;margin-bottom:14px">
            <button type="button" id="mode-set" onclick="document.querySelectorAll('.pw-mode-btn').forEach(b=>b.style.borderColor='#e5e7eb');this.style.borderColor='#1a5276';document.getElementById('swal-pwmode').value='SET';document.getElementById('set-wrap').style.display='block';document.getElementById('link-wrap').style.display='none'" class="pw-mode-btn" style="flex:1;padding:10px 6px;border-radius:10px;border:2px solid #1a5276;background:#eff6ff;color:#1a5276;font-weight:600;cursor:pointer;font-size:0.82rem">Set New Password</button>
            <button type="button" id="mode-link" onclick="document.querySelectorAll('.pw-mode-btn').forEach(b=>b.style.borderColor='#e5e7eb');this.style.borderColor='#16a34a';document.getElementById('swal-pwmode').value='LINK';document.getElementById('set-wrap').style.display='none';document.getElementById('link-wrap').style.display='block'" class="pw-mode-btn" style="flex:1;padding:10px 6px;border-radius:10px;border:2px solid #e5e7eb;background:#fff;color:#6b7280;cursor:pointer;font-size:0.82rem">Email Reset Link</button>
          </div>
          <input type="hidden" id="swal-pwmode" value="SET">
          <div id="set-wrap">
            <label style="font-size:0.82rem;font-weight:600;color:#374151;display:block;margin-bottom:4px">New Password (min 8 characters)</label>
            <input id="swal-newpwd" class="swal2-input" type="text" style="margin:0 0 10px" placeholder="Enter new password">
            <label style="font-size:0.82rem;color:#374151;display:flex;align-items:center;gap:6px">
              <input type="checkbox" id="swal-notify" style="width:auto"> Email the user their new password
            </label>
          </div>
          <div id="link-wrap" style="display:none;font-size:0.82rem;color:#6b7280">
            A password reset link will be emailed to the user's registered email address. The link expires in 1 hour.
          </div>
        </div>`,
      showCancelButton: true,
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#1a5276',
      preConfirm: () => {
        const mode = document.getElementById('swal-pwmode').value;
        if (mode === 'SET') {
          const pwd = document.getElementById('swal-newpwd').value;
          const notifyUser = document.getElementById('swal-notify').checked;
          if (!pwd || pwd.length < 8) { Swal.showValidationMessage('Password must be at least 8 characters'); return false; }
          return { mode, password: pwd, notifyUser };
        }
        return { mode };
      },
      customClass: { popup: 'swal-popup-rounded' },
    }),

  settle: () =>
    Swal.fire({
      title: 'Process Settlement',
      html: `
        <div style="text-align:left">
          <label style="font-size:0.85rem;font-weight:600;color:#374151;display:block;margin-bottom:6px">Settlement Mode</label>
          <select id="swal-mode" class="swal2-input" style="margin:0 0 12px;height:42px">
            <option>Bank Transfer</option><option>Cash</option><option>Cheque</option><option>UPI</option>
          </select>
          <label style="font-size:0.85rem;font-weight:600;color:#374151;display:block;margin-bottom:6px">Reference / Transaction No.</label>
          <input id="swal-ref" class="swal2-input" style="margin:0" placeholder="e.g. TXN-20240510-001">
        </div>`,
      showCancelButton: true,
      confirmButtonText: 'Confirm Settlement',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#16a34a',
      cancelButtonColor: '#6b7280',
      preConfirm: () => ({
        mode: document.getElementById('swal-mode').value,
        ref:  document.getElementById('swal-ref').value,
      }),
      customClass: { popup: 'swal-popup-rounded' },
    }),

  financeProcess: (amount) =>
    Swal.fire({
      title: 'Process Payment',
      html: `
        <div style="text-align:left">
          <div style="background:#f0fdf4;border-radius:8px;padding:12px;margin-bottom:16px;font-size:0.85rem;color:#374151">
            <b>Requested:</b> ${amount}
          </div>
          <label style="font-size:0.85rem;font-weight:600;color:#374151;display:block;margin-bottom:8px">Payment Type</label>
          <div style="display:flex;gap:8px;margin-bottom:12px">
            <button type="button" id="pay-full" onclick="document.querySelectorAll('.pay-type-btn').forEach(b=>b.style.borderColor='#e5e7eb');this.style.borderColor='#16a34a';document.getElementById('swal-paytype').value='FULL';document.getElementById('partial-wrap').style.display='none';document.getElementById('hold-wrap').style.display='none'" class="pay-type-btn" style="flex:1;padding:10px 6px;border-radius:10px;border:2px solid #16a34a;background:#f0fdf4;color:#16a34a;font-weight:600;cursor:pointer;font-size:0.82rem">Full Payment</button>
            <button type="button" id="pay-partial" onclick="document.querySelectorAll('.pay-type-btn').forEach(b=>b.style.borderColor='#e5e7eb');this.style.borderColor='#f59e0b';document.getElementById('swal-paytype').value='PARTIAL';document.getElementById('partial-wrap').style.display='block';document.getElementById('hold-wrap').style.display='none'" class="pay-type-btn" style="flex:1;padding:10px 6px;border-radius:10px;border:2px solid #e5e7eb;background:#fff;color:#6b7280;cursor:pointer;font-size:0.82rem">Partial</button>
            <button type="button" id="pay-hold" onclick="document.querySelectorAll('.pay-type-btn').forEach(b=>b.style.borderColor='#e5e7eb');this.style.borderColor='#ef4444';document.getElementById('swal-paytype').value='HOLD';document.getElementById('partial-wrap').style.display='none';document.getElementById('hold-wrap').style.display='block'" class="pay-type-btn" style="flex:1;padding:10px 6px;border-radius:10px;border:2px solid #e5e7eb;background:#fff;color:#6b7280;cursor:pointer;font-size:0.82rem">Hold</button>
          </div>
          <input type="hidden" id="swal-paytype" value="FULL">
          <div id="partial-wrap" style="display:none;margin-bottom:10px">
            <label style="font-size:0.82rem;font-weight:600;color:#374151;display:block;margin-bottom:4px">Partial Amount (₹)</label>
            <input id="swal-partial" class="swal2-input" type="number" style="margin:0" placeholder="Enter partial amount">
          </div>
          <div id="hold-wrap" style="display:none">
            <label style="font-size:0.82rem;font-weight:600;color:#374151;display:block;margin-bottom:4px">Hold Reason</label>
            <textarea id="swal-holdreason" class="swal2-textarea" style="margin:0" placeholder="e.g. GRN/Material incomplete"></textarea>
          </div>
        </div>`,
      showCancelButton: true,
      confirmButtonText: 'Process Payment',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#1a5276',
      preConfirm: () => {
        const payType = document.getElementById('swal-paytype').value;
        const partialAmt = document.getElementById('swal-partial')?.value;
        const holdReason = document.getElementById('swal-holdreason')?.value;
        if (payType === 'PARTIAL' && !partialAmt) { Swal.showValidationMessage('Enter partial amount'); return false; }
        if (payType === 'HOLD' && !holdReason?.trim()) { Swal.showValidationMessage('Hold reason required'); return false; }
        return { payType, partialAmt: partialAmt ? Number(partialAmt) : undefined, holdReason };
      },
      customClass: { popup: 'swal-popup-rounded' },
    }),

  // Approve a Travel Advance Request — optionally revise the amount (Reporting
  // Manager / Finance stage only). The employee will not see this revision until
  // the request is fully approved.
  approveAdvance: (currentAmount) =>
    Swal.fire({
      title: 'Approve Advance Request',
      html: `
        <div style="text-align:left">
          <div style="background:#f0fdf4;border-radius:8px;padding:12px;margin-bottom:14px;font-size:0.85rem;color:#374151">
            <b>Current Amount:</b> ₹${Number(currentAmount || 0).toLocaleString('en-IN')}
          </div>
          <label style="font-size:0.82rem;font-weight:600;color:#374151;display:block;margin-bottom:4px">Approve Amount (₹) — leave unchanged unless revising</label>
          <input id="swal-amount" class="swal2-input" type="number" style="margin:0 0 12px" value="${Number(currentAmount || 0)}">
          <label style="font-size:0.82rem;font-weight:600;color:#374151;display:block;margin-bottom:4px">Remarks (optional)</label>
          <textarea id="swal-remarks" class="swal2-textarea" style="margin:0" placeholder="Approval remarks..."></textarea>
        </div>`,
      showCancelButton: true,
      confirmButtonText: 'Approve',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#16a34a',
      preConfirm: () => {
        const amount = document.getElementById('swal-amount').value;
        const remarks = document.getElementById('swal-remarks').value;
        if (amount === '' || Number(amount) < 0) { Swal.showValidationMessage('Enter a valid amount'); return false; }
        if (Number(amount) > Number(currentAmount)) { Swal.showValidationMessage('Amount cannot exceed the original requested amount'); return false; }
        return { revisedAmount: Number(amount), remarks };
      },
      customClass: { popup: 'swal-popup-rounded' },
    }),

  // Approve an Expense Claim — optionally revise the net payable amount
  // (Manager / Finance stage only). The employee will not see this revision
  // until the claim reaches a terminal state.
  approveClaim: (currentAmount) =>
    Swal.fire({
      title: 'Approve Expense Claim',
      html: `
        <div style="text-align:left">
          <div style="background:#f0fdf4;border-radius:8px;padding:12px;margin-bottom:14px;font-size:0.85rem;color:#374151">
            <b>Current Net Payable:</b> ₹${Number(currentAmount || 0).toLocaleString('en-IN')}
          </div>
          <label style="font-size:0.82rem;font-weight:600;color:#374151;display:block;margin-bottom:4px">Approve Amount (₹) — leave unchanged unless revising</label>
          <input id="swal-camount" class="swal2-input" type="number" style="margin:0 0 12px" value="${Number(currentAmount || 0)}">
          <label style="font-size:0.82rem;font-weight:600;color:#374151;display:block;margin-bottom:4px">Remarks (optional, required if revising)</label>
          <textarea id="swal-cremarks" class="swal2-textarea" style="margin:0" placeholder="Approval remarks..."></textarea>
        </div>`,
      showCancelButton: true,
      confirmButtonText: 'Approve',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#16a34a',
      preConfirm: () => {
        const amount = document.getElementById('swal-camount').value;
        const remarks = document.getElementById('swal-cremarks').value;
        if (amount === '') { Swal.showValidationMessage('Enter a valid amount'); return false; }
        if (Number(amount) > Number(currentAmount)) { Swal.showValidationMessage('Amount cannot exceed the original net payable amount'); return false; }
        return { revisedAmount: Number(amount), remarks };
      },
      customClass: { popup: 'swal-popup-rounded' },
    }),

  // Admin updates a complaint's status (Mark In Review / Resolve / Reject),
  // with an optional (required for Resolve/Reject) remarks note that gets
  // emailed back to the complainant.
  complaintStatus: (nextStatus, currentRemarks = '') => {
    const labels = { IN_REVIEW: 'Mark In Review', RESOLVED: 'Resolve Complaint', REJECTED: 'Reject Complaint' };
    const colors = { IN_REVIEW: '#2563eb', RESOLVED: '#16a34a', REJECTED: '#dc2626' };
    const remarksRequired = nextStatus === 'RESOLVED' || nextStatus === 'REJECTED';
    return Swal.fire({
      title: labels[nextStatus] || 'Update Status',
      html: `
        <div style="text-align:left">
          <label style="font-size:0.82rem;font-weight:600;color:#374151;display:block;margin-bottom:4px">
            Remarks ${remarksRequired ? '*' : '(optional)'}
          </label>
          <textarea id="swal-cstatus-remarks" class="swal2-textarea" style="margin:0" placeholder="${remarksRequired ? 'Explain how this was resolved / why it was rejected...' : 'Optional note...'}">${currentRemarks || ''}</textarea>
        </div>`,
      showCancelButton: true,
      confirmButtonText: labels[nextStatus] || 'Update',
      cancelButtonText: 'Cancel',
      confirmButtonColor: colors[nextStatus] || '#1a5276',
      preConfirm: () => {
        const remarks = document.getElementById('swal-cstatus-remarks').value;
        if (remarksRequired && !remarks.trim()) {
          Swal.showValidationMessage('Remarks are required');
          return false;
        }
        return { remarks };
      },
      customClass: { popup: 'swal-popup-rounded' },
    });
  },
};
